import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, EnumDecoder, field, IntegerDecoder, MapDecoder, NumberDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { v4 as uuidv4 } from 'uuid';
import { Colors, Formatter } from '@stamhoofd/utility';
import { DefaultAgeGroup } from './DefaultAgeGroup.js';
import { Replacement } from './endpoints/EmailRequest.js';
import { EventNotificationType } from './EventNotificationType.js';
import { Image } from './files/Image.js';
import { LoginMethod, LoginMethodConfig } from './LoginMethod.js';
import { MemberResponsibility } from './MemberResponsibility.js';
import { DataPermissionsSettings, FinancialSupportSettings, OrganizationRecordsConfiguration } from './members/OrganizationRecordsConfiguration.js';
import { OrganizationEmail } from './OrganizationEmail.js';
import { OrganizationLevelRecordsConfiguration } from './OrganizationLevelRecordsConfiguration.js';
import { PermissionRoleDetailed } from './PermissionRole.js';
import { ReduceablePrice } from './ReduceablePrice.js';
import { RegistrationPeriod } from './RegistrationPeriod.js';
import { RichText } from './RichText.js';
import { upgradePriceFrom2To4DecimalPlaces } from './upgradePriceFrom2To4DecimalPlaces.js';
export class PlatformPrivateConfig extends AutoEncoder {
    roles = [];
    emails = [];
}
__decorate([
    field({ decoder: new ArrayDecoder(PermissionRoleDetailed) })
], PlatformPrivateConfig.prototype, "roles", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(OrganizationEmail), version: 272 })
], PlatformPrivateConfig.prototype, "emails", void 0);
export var OrganizationTagType;
(function (OrganizationTagType) {
    OrganizationTagType["Tag"] = "Tag";
    OrganizationTagType["Verbond"] = "Verbond";
    OrganizationTagType["Gewest"] = "Gewest";
    OrganizationTagType["Gouw"] = "Gouw";
})(OrganizationTagType || (OrganizationTagType = {}));
export function getOrganizationTagTypeName(type) {
    switch (type) {
        case OrganizationTagType.Tag:
            return $t(`%2T`);
        case OrganizationTagType.Verbond:
            return $t(`%n5`);
        case OrganizationTagType.Gewest:
            return $t(`%n6`);
        case OrganizationTagType.Gouw:
            return $t(`%n7`);
    }
}
export function getOrganizationTagTypePluralName(type) {
    switch (type) {
        case OrganizationTagType.Tag:
            return $t(`%2C`);
        case OrganizationTagType.Verbond:
            return $t(`%n8`);
        case OrganizationTagType.Gewest:
            return $t(`%n9`);
        case OrganizationTagType.Gouw:
            return $t(`%nA`);
    }
}
export class OrganizationTag extends AutoEncoder {
    id;
    name = '';
    description = '';
    organizationCount = 0;
    childTags = [];
    get type() {
        if (this.name.startsWith('Verbond ')) {
            return OrganizationTagType.Verbond;
        }
        if (this.name.startsWith('Gewest ')) {
            return OrganizationTagType.Gewest;
        }
        return OrganizationTagType.Tag;
    }
    getChildType(allTags) {
        if (this.childTags.length === 0) {
            return OrganizationTagType.Tag;
        }
        const childTag = allTags.find(t => t.id === this.childTags[0]);
        if (!childTag) {
            return OrganizationTagType.Tag;
        }
        return childTag.type;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], OrganizationTag.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], OrganizationTag.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 341 })
], OrganizationTag.prototype, "description", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 341 })
], OrganizationTag.prototype, "organizationCount", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder), version: 341 })
], OrganizationTag.prototype, "childTags", void 0);
export class PlatformPremiseType extends AutoEncoder {
    id;
    name = '';
    description = '';
    /**
     * Maximum number of premises allowed for this premise type
     */
    max = null;
    /**
    * Minimum number of premises allowed for this premise type
    */
    min = null;
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], PlatformPremiseType.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], PlatformPremiseType.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 319 })
], PlatformPremiseType.prototype, "description", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 319 })
], PlatformPremiseType.prototype, "max", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 319 })
], PlatformPremiseType.prototype, "min", void 0);
export class PlatformMembershipTypeConfigPrice extends AutoEncoder {
    id;
    startDate = null;
    /**
     *@deprecated
     */
    _price = 0;
    // key = tag id or an empty string for the default reducable price
    prices = new Map([['', ReduceablePrice.create({ price: 0 })]]);
    /**
     * If you set this, it will be possible to choose a custom start and end date within the startDate - endDate period
     */
    pricePerDay = 0;
    getBasePrice(tagIds, shouldApplyReducedPrice) {
        let result = null;
        for (const tagId of tagIds.concat([''])) {
            const price = this.prices.get(tagId);
            if (!price)
                continue;
            const priceForMember = price.getPrice(shouldApplyReducedPrice);
            if (result === null || priceForMember < result) {
                result = priceForMember;
            }
        }
        return result ?? 0;
    }
    calculatePrice(tagIds, shouldApplyReducedPrice, days) {
        const basePrice = this.getBasePrice(tagIds, shouldApplyReducedPrice);
        return this.pricePerDay * days + basePrice;
    }
    get name() {
        if (this.startDate) {
            return $t(`%nB`) + ' ' + Formatter.date(this.startDate);
        }
        return $t(`%nC`);
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], PlatformMembershipTypeConfigPrice.prototype, "id", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], PlatformMembershipTypeConfigPrice.prototype, "startDate", void 0);
__decorate([
    field({ decoder: IntegerDecoder, field: 'price', optional: true })
], PlatformMembershipTypeConfigPrice.prototype, "_price", void 0);
__decorate([
    field({ decoder: new MapDecoder(StringDecoder, ReduceablePrice), version: 329 })
], PlatformMembershipTypeConfigPrice.prototype, "prices", void 0);
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], PlatformMembershipTypeConfigPrice.prototype, "pricePerDay", void 0);
export class PlatformMembershipTypeConfig extends AutoEncoder {
    startDate = new Date();
    endDate = new Date();
    expireDate = null;
    amountFree = 0;
    trialDays = 0;
    maximumDays = null;
    prices = [PlatformMembershipTypeConfigPrice.create({})];
    /**
     * Returns the latest allowed membership end date. Days memberships count both start and end date as included days.
     */
    getMaximumEndDate(startDate, behaviour) {
        if (behaviour === PlatformMembershipTypeBehaviour.Period || this.maximumDays === null) {
            return Formatter.luxon(this.endDate)
                .set({ hour: 23, minute: 59, second: 59, millisecond: 0 })
                .toJSDate();
        }
        const maxEndDate = Formatter.luxon(startDate)
            .plus({ days: this.maximumDays - 1 })
            .set({ hour: 23, minute: 59, second: 59, millisecond: 0 })
            .toJSDate();
        return maxEndDate < this.endDate ? maxEndDate : this.endDate;
    }
    getPriceConfigForDate(date) {
        if (date === undefined) {
            throw new Error('Date is required');
        }
        const sorted = this.prices.slice().sort((a, b) => (a.startDate ?? new Date(0)).getTime() - (b.startDate ?? new Date(0)).getTime());
        let result = sorted[0];
        for (const priceConfig of sorted) {
            if (priceConfig.startDate === null || date >= priceConfig.startDate) {
                result = priceConfig;
            }
        }
        return result;
    }
    getPrice(date, tagIds, shouldApplyReducedPrice) {
        return this.getPriceConfigForDate(date).getBasePrice(tagIds, shouldApplyReducedPrice);
    }
    get name() {
        return Formatter.dateRange(this.startDate, this.endDate);
    }
}
__decorate([
    field({ decoder: DateDecoder })
], PlatformMembershipTypeConfig.prototype, "startDate", void 0);
__decorate([
    field({ decoder: DateDecoder })
], PlatformMembershipTypeConfig.prototype, "endDate", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], PlatformMembershipTypeConfig.prototype, "expireDate", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], PlatformMembershipTypeConfig.prototype, "amountFree", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 354 })
], PlatformMembershipTypeConfig.prototype, "trialDays", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, ...NextVersion })
], PlatformMembershipTypeConfig.prototype, "maximumDays", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(PlatformMembershipTypeConfigPrice) })
], PlatformMembershipTypeConfig.prototype, "prices", void 0);
export var PlatformMembershipTypeBehaviour;
(function (PlatformMembershipTypeBehaviour) {
    /**
     * A membership that is valid for a certain period
     */
    PlatformMembershipTypeBehaviour["Period"] = "Period";
    /**
     * A membership that is valid for a certain number of days
     */
    PlatformMembershipTypeBehaviour["Days"] = "Days";
})(PlatformMembershipTypeBehaviour || (PlatformMembershipTypeBehaviour = {}));
export class PlatformMembershipType extends AutoEncoder {
    id;
    name = '';
    description = '';
    behaviour = PlatformMembershipTypeBehaviour.Period;
    /**
     * Settings per period
     */
    periods = new Map();
    /**
     * Only allow organizations with these tags to use this membership type
     */
    requiredTagIds = null;
    /**
     * Only allow groups with these default age groups to use this membership type
     */
    requiredDefaultAgeGroupIds = null;
    getPrice(periodId, date, tagIds, isReduced) {
        const period = this.periods.get(periodId);
        if (!period) {
            return null;
        }
        return period.getPrice(date, tagIds, isReduced);
    }
    isEnabled(tagIds, defaultAgeGroupIds) {
        return this.isEnabledForDefaultAgeGroup(defaultAgeGroupIds) && this.isEnabledForTags(tagIds);
    }
    isEnabledForDefaultAgeGroup(defaultAgeGroupIds) {
        const requiredDefaultAgeGroupIds = this.requiredDefaultAgeGroupIds;
        if (requiredDefaultAgeGroupIds === null) {
            return true;
        }
        return defaultAgeGroupIds.find(id => requiredDefaultAgeGroupIds.includes(id)) !== undefined;
    }
    isEnabledForTags(tags) {
        const requiredTags = this.requiredTagIds;
        if (requiredTags === null) {
            return true;
        }
        return tags.find(tagId => requiredTags.includes(tagId)) !== undefined;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], PlatformMembershipType.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], PlatformMembershipType.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder })
], PlatformMembershipType.prototype, "description", void 0);
__decorate([
    field({ decoder: new EnumDecoder(PlatformMembershipTypeBehaviour) })
], PlatformMembershipType.prototype, "behaviour", void 0);
__decorate([
    field({ decoder: new MapDecoder(StringDecoder, PlatformMembershipTypeConfig) })
], PlatformMembershipType.prototype, "periods", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder), nullable: true })
], PlatformMembershipType.prototype, "requiredTagIds", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder), nullable: true })
], PlatformMembershipType.prototype, "requiredDefaultAgeGroupIds", void 0);
export class PlatformEventType extends AutoEncoder {
    id;
    name = '';
    description = '';
    /**
     * Maximum amount that each organization can have this type
     */
    maximum = null;
    /**
     * Maximum number of days allowed for this event type
     */
    maximumDays = null;
    /**
     * Minimum number of days allowed for this event type
     */
    minimumDays = null;
    isLocationRequired = false;
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], PlatformEventType.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], PlatformEventType.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder })
], PlatformEventType.prototype, "description", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true })
], PlatformEventType.prototype, "maximum", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 288 })
], PlatformEventType.prototype, "maximumDays", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 288 })
], PlatformEventType.prototype, "minimumDays", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 334 })
], PlatformEventType.prototype, "isLocationRequired", void 0);
export class PlatformPolicy extends AutoEncoder {
    id;
    name = '';
    url = '';
    enableAtSignup = true;
    checkbox = true;
    richText = RichText.create({});
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], PlatformPolicy.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], PlatformPolicy.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder })
], PlatformPolicy.prototype, "url", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 327 })
], PlatformPolicy.prototype, "enableAtSignup", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 327 })
], PlatformPolicy.prototype, "checkbox", void 0);
__decorate([
    field({ decoder: RichText, version: 327 })
], PlatformPolicy.prototype, "richText", void 0);
export class PrivacySettings extends AutoEncoder {
    policies = [];
}
__decorate([
    field({ decoder: new ArrayDecoder(PlatformPolicy) })
], PrivacySettings.prototype, "policies", void 0);
export class PlatformConfig extends AutoEncoder {
    name = 'Stamhoofd';
    featureFlags = [];
    /**
     * Contains the text and settings for when financial support is enabled - not whether it is enabled
     */
    financialSupport = null;
    /**
     * Contains the text and settings for when data permissions are enabled - not whether it is enabled
     */
    dataPermission = null;
    recordsConfiguration = OrganizationRecordsConfiguration.create({});
    organizationLevelRecordsConfiguration = OrganizationLevelRecordsConfiguration.create({});
    tags = [];
    premiseTypes = [];
    defaultAgeGroups = [];
    responsibilities = [];
    membershipTypes = [];
    eventTypes = [];
    eventNotificationTypes = [];
    coverPhoto = null;
    coverBottomLeftOverlayImage = null;
    coverBottomLeftOverlayWidth = 150;
    footerText = RichText.create({});
    shopFooterText = RichText.create({});
    color = null;
    horizontalLogoDark = null;
    squareLogoDark = null;
    horizontalLogo = null;
    squareLogo = null;
    /**
     * This is not the logo of the platform, but of the corresponding organization (e.g. KSA)
     * Optional. When not set, it will use the squareLogo
     */
    organizationLogo = null;
    organizationLogoDark = null;
    logoDocuments = null;
    expandLogo = false;
    privacy = PrivacySettings.create({});
    loginMethods = new Map([[
            LoginMethod.Password,
            LoginMethodConfig.create({}),
        ]]);
    getEmailReplacements(platform, isPreviewing = false) {
        const base = [
            Replacement.create({
                token: 'primaryColor',
                value: this.color ? this.color : '#0053ff',
            }),
            Replacement.create({
                token: 'primaryColorContrast',
                value: this.color ? Colors.getContrastColor(this.color) : '#fff',
            }),
        ];
        const fromAddress = platform.privateConfig?.emails?.find(e => e.default) || platform.privateConfig?.emails[0];
        if (fromAddress) {
            base.push(Replacement.create({
                token: 'fromAddress',
                value: fromAddress.email,
            }));
            base.push(Replacement.create({
                token: 'fromName',
                value: fromAddress.name ?? this.name,
            }));
        }
        else {
            base.push(Replacement.create({
                token: 'fromName',
                value: this.name,
            }));
        }
        if (!isPreviewing) {
            // Add organizationName fallback
            base.push(Replacement.create({
                token: 'organizationName',
                value: this.name,
            }));
        }
        base.push(Replacement.create({
            token: 'platformName',
            value: this.name,
        }));
        base.push(Replacement.create({
            token: 'registerUrl',
            value: 'https://' + STAMHOOFD.domains.dashboard,
        }));
        return base;
    }
    getEnabledPlatformMembershipTypes(tagIds, defaultAgeGroupIds) {
        return this.membershipTypes.filter(type => type.isEnabled(tagIds, defaultAgeGroupIds));
    }
}
__decorate([
    field({ decoder: StringDecoder, version: 326 })
], PlatformConfig.prototype, "name", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder), version: 348 })
], PlatformConfig.prototype, "featureFlags", void 0);
__decorate([
    field({ decoder: FinancialSupportSettings, nullable: true, version: 320 })
], PlatformConfig.prototype, "financialSupport", void 0);
__decorate([
    field({ decoder: DataPermissionsSettings, nullable: true, version: 320 })
], PlatformConfig.prototype, "dataPermission", void 0);
__decorate([
    field({ decoder: OrganizationRecordsConfiguration, version: 253 })
], PlatformConfig.prototype, "recordsConfiguration", void 0);
__decorate([
    field({ decoder: OrganizationLevelRecordsConfiguration, version: 358 })
], PlatformConfig.prototype, "organizationLevelRecordsConfiguration", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(OrganizationTag), version: 260 })
], PlatformConfig.prototype, "tags", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(PlatformPremiseType), version: 319 })
], PlatformConfig.prototype, "premiseTypes", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(DefaultAgeGroup), version: 261 })
], PlatformConfig.prototype, "defaultAgeGroups", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(MemberResponsibility), version: 262 })
], PlatformConfig.prototype, "responsibilities", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(PlatformMembershipType), version: 268 })
], PlatformConfig.prototype, "membershipTypes", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(PlatformEventType), version: 287 })
], PlatformConfig.prototype, "eventTypes", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(EventNotificationType), version: 362 })
], PlatformConfig.prototype, "eventNotificationTypes", void 0);
__decorate([
    field({ decoder: Image, nullable: true, version: 310 })
], PlatformConfig.prototype, "coverPhoto", void 0);
__decorate([
    field({ decoder: Image, nullable: true, version: 332 })
], PlatformConfig.prototype, "coverBottomLeftOverlayImage", void 0);
__decorate([
    field({ decoder: NumberDecoder, version: 332 })
], PlatformConfig.prototype, "coverBottomLeftOverlayWidth", void 0);
__decorate([
    field({ decoder: RichText, version: 332 })
], PlatformConfig.prototype, "footerText", void 0);
__decorate([
    field({ decoder: RichText, version: 342 })
], PlatformConfig.prototype, "shopFooterText", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 310 })
], PlatformConfig.prototype, "color", void 0);
__decorate([
    field({ decoder: Image, nullable: true, version: 310 })
], PlatformConfig.prototype, "horizontalLogoDark", void 0);
__decorate([
    field({ decoder: Image, nullable: true, version: 310 })
], PlatformConfig.prototype, "squareLogoDark", void 0);
__decorate([
    field({ decoder: Image, nullable: true, version: 310 })
], PlatformConfig.prototype, "horizontalLogo", void 0);
__decorate([
    field({ decoder: Image, nullable: true, version: 310 })
], PlatformConfig.prototype, "squareLogo", void 0);
__decorate([
    field({ decoder: Image, nullable: true, version: 384 })
], PlatformConfig.prototype, "organizationLogo", void 0);
__decorate([
    field({ decoder: Image, nullable: true, version: 384 })
], PlatformConfig.prototype, "organizationLogoDark", void 0);
__decorate([
    field({ decoder: Image, nullable: true, version: 348 })
], PlatformConfig.prototype, "logoDocuments", void 0);
__decorate([
    field({ decoder: BooleanDecoder, optional: true, version: 310 })
], PlatformConfig.prototype, "expandLogo", void 0);
__decorate([
    field({ decoder: PrivacySettings, version: 327 })
], PlatformConfig.prototype, "privacy", void 0);
__decorate([
    field({ decoder: new MapDecoder(new EnumDecoder(LoginMethod), LoginMethodConfig), version: 361 })
], PlatformConfig.prototype, "loginMethods", void 0);
export class Platform extends AutoEncoder {
    static instance = null;
    config = PlatformConfig.create({});
    privateConfig = null;
    period = RegistrationPeriod.create({});
    /**
     * The organization that represents the platform for collection of platform memberships payments
     */
    membershipOrganizationId = null;
    /**
     * @deprecated
     * This way of caching is discouraged and unstable because it gets overriden easily using deepSet
     */
    periods;
    /**
     * If you don't have permissions, privateConfig will be null, so there won't be any roles either
     */
    getRoles() {
        return this.privateConfig?.roles ?? [];
    }
    static get shared() {
        if (!Platform.instance) {
            Platform.instance = Platform.create({});
        }
        return Platform.instance;
    }
    static get optionalShared() {
        return Platform.instance;
    }
    static clearShared() {
        Platform.instance = null;
    }
    setShared() {
        Platform.instance = this;
    }
}
__decorate([
    field({ decoder: PlatformConfig })
], Platform.prototype, "config", void 0);
__decorate([
    field({ decoder: PlatformPrivateConfig, nullable: true })
], Platform.prototype, "privateConfig", void 0);
__decorate([
    field({ decoder: RegistrationPeriod })
], Platform.prototype, "period", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 324 })
], Platform.prototype, "membershipOrganizationId", void 0);
//# sourceMappingURL=Platform.js.map